import apiService from './apiService';

class VoiceFlowService {

  // ============================================================
  // 範本 API
  // ============================================================

  async listTemplates(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.industry) queryParams.append('industry', params.industry);
      if (params.triggerType) queryParams.append('trigger_type', params.triggerType);
      const qs = queryParams.toString();
      const endpoint = `voice-flow-templates${qs ? `?${qs}` : ''}`;
      const response = await apiService.apiRequest('get', endpoint);
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, message: response.data?.message || '取得範本列表失敗' };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || '取得範本列表請求失敗' };
    }
  }

  async getTemplate(templateId) {
    try {
      const response = await apiService.apiRequest('get', `voice-flow-templates/${templateId}`);
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, message: response.data?.message || '取得範本失敗' };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || '取得範本請求失敗' };
    }
  }

  async applyTemplate(templateId, data) {
    try {
      const response = await apiService.apiRequest('post', `voice-flow-templates/${templateId}/apply`, data);
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, message: response.data?.message || '套用範本失敗' };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || '套用範本請求失敗' };
    }
  }

  // ============================================================
  // 流程實例 API
  // ============================================================

  async listFlows(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.triggerType) queryParams.append('trigger_type', params.triggerType);
      if (params.isActive !== undefined) queryParams.append('is_active', params.isActive);
      const qs = queryParams.toString();
      const endpoint = `voice-flows${qs ? `?${qs}` : ''}`;
      const response = await apiService.apiRequest('get', endpoint);
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, message: response.data?.message || '取得流程列表失敗' };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || '取得流程列表請求失敗' };
    }
  }

  async getFlow(flowId) {
    try {
      const response = await apiService.apiRequest('get', `voice-flows/${flowId}`);
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, message: response.data?.message || '取得流程失敗' };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || '取得流程請求失敗' };
    }
  }

  async updateFlow(flowId, data) {
    try {
      const response = await apiService.apiRequest('put', `voice-flows/${flowId}`, data);
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, message: response.data?.message || '更新流程失敗' };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || '更新流程請求失敗' };
    }
  }

  async deleteFlow(flowId) {
    try {
      const response = await apiService.apiRequest('delete', `voice-flows/${flowId}`);
      if (response.data?.success) {
        return { success: true };
      }
      return { success: false, message: response.data?.message || '刪除流程失敗' };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || '刪除流程請求失敗' };
    }
  }

  async toggleFlow(flowId) {
    try {
      const response = await apiService.apiRequest('patch', `voice-flows/${flowId}/toggle`);
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, message: response.data?.message || '切換流程狀態失敗' };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || '切換流程狀態請求失敗' };
    }
  }

  async duplicateFlow(flowId, data) {
    try {
      const response = await apiService.apiRequest('post', `voice-flows/${flowId}/duplicate`, data);
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, message: response.data?.message || '複製流程失敗' };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || '複製流程請求失敗' };
    }
  }

  // ============================================================
  // 腳本 API（Script Editor 使用）
  // ============================================================

  async getScript(flowId) {
    try {
      const response = await apiService.apiRequest('get', `voice-flows/${flowId}/script`);
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, message: response.data?.message || '取得腳本失敗' };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || '取得腳本請求失敗' };
    }
  }

  async updateScript(flowId, edits) {
    try {
      const response = await apiService.apiRequest('put', `voice-flows/${flowId}/script`, edits);
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, message: response.data?.message || '儲存腳本失敗' };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || '儲存腳本請求失敗' };
    }
  }

  // ============================================================
  // TTS 語音產生 API（ScriptBubble 自動觸發 + ScriptEditor 批次）
  // ============================================================

  async regenerateAudio(flowId, nodes, options = {}) {
    try {
      const response = await apiService.apiRequest('post', `voice-flows/${flowId}/regenerate-audio`, {
        nodes,
        speed: options.speed
      });
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, message: response.data?.message || 'TTS 產生失敗' };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'TTS 產生請求失敗' };
    }
  }
}

const voiceFlowService = new VoiceFlowService();
export default voiceFlowService;
